import React, { useEffect, useState } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { useAuth } from "../../../context/auth-context";
import PrivateRoute from "../../../components/utils/PrivateRoute";
import KycVerificationPage from "./KycOnboarding";
import { loaderGif } from "../../../assets/images";
import { setHandoffToken } from "../../../utils/handoffToken";

type AuthStatus = "idle" | "pending" | "done" | "error";

const KycVerificationGate = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { refreshSession, logout, isAuthenticated, authReady } = useAuth();

  const tokenFromUrl = searchParams.get("token");
  const [authStatus, setAuthStatus] = useState<AuthStatus>("idle");

  useEffect(() => {
    if (!authReady) return;
    // If there's already a valid web session, just drop the query param and show the page.
    if (tokenFromUrl && isAuthenticated) {
      try {
        window.history.replaceState({}, "", "/kyc-verification");
      } catch {
        // ignore
      }
      setSearchParams({}, { replace: true });
      return;
    }
    if (!tokenFromUrl) return;
    if (authStatus !== "idle") return;

    let cancelled = false;

    const runAuth = async () => {
      try {
        setAuthStatus("pending");

        // ⚠️ Mobile hands its bearer token over in the URL. Under cookie auth we can't turn
        // that into a session — only the backend can mint an httpOnly cookie. Until there's
        // a bearer→cookie exchange endpoint, this one flow stays on bearer.
        // See utils/handoffToken.ts.
        setHandoffToken(tokenFromUrl);
        const ok = await refreshSession();

        if (cancelled) return;
        if (!ok) throw new Error("Handoff token was not accepted.");

        setAuthStatus("done");

        // remove token from url after successful auth
        try {
          // Fallback for cases where router doesn't re-render on same-path replace
          window.history.replaceState({}, "", "/kyc-verification");
        } catch {
          // ignore
        }
        setSearchParams({}, { replace: true });
      } catch (error) {
        if (cancelled) return;

        logout();
        setAuthStatus("error");
      }
    };

    runAuth();

    return () => {
      cancelled = true;
    };
  }, [
    authReady,
    tokenFromUrl,
    authStatus,
    refreshSession,
    logout,
    setSearchParams,
    isAuthenticated,
  ]);

  if (!authReady) {
    return (
      <div className="fixed inset-0 flex font-manrope justify-center items-center bg-white dark:bg-black dark:bg-opacity-80 bg-opacity-70 backdrop-blur-sm">
        <div className="flex flex-col items-center justify-center text-center max-w-sm px-6">
          <div className="w-[120px] h-[120px] shrink-0">
            <img
              src={loaderGif}
              alt=""
              className="w-full h-full object-contain"
            />
          </div>
          <h2 className="mt-8 text-xl font-semibold text-gray-900 dark:text-white">
            Preparing session...
          </h2>
        </div>
      </div>
    );
  }

  // if token auth from url has completed successfully, render page immediately
  if (authStatus === "done" && isAuthenticated) {
    return <KycVerificationPage />;
  }

  // normal direct visit without token in url
  if (!tokenFromUrl) {
    return (
      <PrivateRoute>
        <KycVerificationPage />
      </PrivateRoute>
    );
  }

  if (authStatus === "error") {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="fixed inset-0 flex font-manrope justify-center items-center bg-white dark:bg-black dark:bg-opacity-80 bg-opacity-70 backdrop-blur-sm">
      <div className="flex flex-col items-center justify-center text-center max-w-sm px-6">
        <div className="w-[120px] h-[120px] shrink-0">
          <img
            src={loaderGif}
            alt=""
            className="w-full h-full object-contain"
          />
        </div>
        <h2 className="mt-8 text-xl font-semibold text-gray-900 dark:text-white">
          Authenticating...
        </h2>
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          Please wait while we verify your session. You&apos;ll be redirected
          shortly.
        </p>
      </div>
    </div>
  );
};

export default KycVerificationGate;
